/**
 * Cliente HTTP único da aplicação (equivalente ao services/institutionAPI da Impacto).
 *
 * - A API fica em /api (mesmo domínio): os cookies de sessão são httpOnly e nunca acessíveis ao JS.
 * - Envia o token anti-CSRF (cookie legível az_csrf → header x-csrf-token) em toda mutação.
 * - Em 401 tenta renovar a sessão uma única vez (refresh compartilhado entre chamadas simultâneas).
 * - Erros viram ApiError com { status, code, message, details } prontos para exibir.
 */
export const API_BASE = '/api';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: { path: string; message: string }[];
  constructor(status: number, code: string, message: string, details?: { path: string; message: string }[]) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

function csrfToken() {
  const match = document.cookie.match(/(?:^|;\s*)az_csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]!) : '';
}

let refreshing: Promise<boolean> | null = null;
/** Renova a sessão (refresh token rotativo). Chamadas simultâneas compartilham a mesma tentativa. */
export function refreshSession(): Promise<boolean> {
  refreshing ??= fetch(`${API_BASE}/auth/refresh`, { method: 'POST', credentials: 'include', headers: { 'x-csrf-token': csrfToken() } })
    .then((r) => r.ok)
    .catch(() => false)
    .finally(() => setTimeout(() => (refreshing = null), 0));
  return refreshing;
}

type Options = Omit<RequestInit, 'body'> & { body?: unknown; query?: Record<string, unknown>; skipRefresh?: boolean };

export async function api<T = unknown>(path: string, opts: Options = {}): Promise<T> {
  const url = new URL(API_BASE + path, window.location.origin);
  for (const [k, v] of Object.entries(opts.query ?? {})) {
    if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
  }
  const isForm = opts.body instanceof FormData;
  const method = (opts.method ?? 'GET').toUpperCase();
  const headers: Record<string, string> = { ...(opts.headers as Record<string, string>) };
  if (!isForm && opts.body !== undefined) headers['content-type'] = 'application/json';
  if (method !== 'GET') headers['x-csrf-token'] = csrfToken();

  const res = await fetch(url, {
    ...opts,
    method,
    headers,
    credentials: 'include',
    body: isForm ? (opts.body as FormData) : opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  if (res.status === 401 && !opts.skipRefresh && !path.startsWith('/auth/refresh') && !path.startsWith('/auth/login')) {
    if (await refreshSession()) return api<T>(path, { ...opts, skipRefresh: true });
    window.dispatchEvent(new CustomEvent('azanas:session-expired'));
  }

  const isJson = res.headers.get('content-type')?.includes('application/json');
  const data = isJson ? await res.json().catch(() => null) : null;
  if (!res.ok) {
    const e = data?.error;
    throw new ApiError(res.status, e?.code ?? 'http_error', e?.message ?? 'Não foi possível concluir. Tente novamente.', e?.details);
  }
  return data as T;
}

/** Atalhos */
export const http = {
  get: <T>(path: string, query?: Record<string, unknown>) => api<T>(path, { query }),
  post: <T>(path: string, body?: unknown) => api<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => api<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => api<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => api<T>(path, { method: 'DELETE' }),
  upload: <T>(path: string, form: FormData, method = 'POST') => api<T>(path, { method, body: form }),
};

/** URL para abrir arquivos privados servidos pela API (o cookie de sessão autentica). */
export const fileUrl = (fileId: string, download = false) => `${API_BASE}/files/${fileId}${download ? '?download=1' : ''}`;
